import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { ToastActionType, useDelayedActionToast, BottomBarContext } from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/router";
import { ROUTES } from "../constants";
import { DELAYED_TOAST_BUTTON_TYPES, DELAYED_TOAST_CONSTANTS } from "../constants/delayedToast";
import { IPartnerOrderToast } from "../utils/types";

export const usePartnerOrderToast = () => {
    const router = useRouter();
    const { toastState, dispatch: dispatchDelayedActionToast } = useDelayedActionToast();
    const { setFetchPartnerOrder } = useContext(BottomBarContext);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            dispatchDelayedActionToast({
                type: ToastActionType.HIDE_TOAST,
            });
        };
    }, [dispatchDelayedActionToast]);

    useEffect(() => {
        if (isMountedRef.current) {
            setIsToastVisible(toastState?.isVisible || false);
        }
    }, [toastState]);

    const clickHandling = useCallback(
        (ctaType: string, ctaAction: string) => {
            switch (ctaType) {
                case DELAYED_TOAST_BUTTON_TYPES.SCREEN:
                    router.push(ROUTES[ctaAction as keyof typeof ROUTES]);
                    break;
                case DELAYED_TOAST_BUTTON_TYPES.CANCEL:
                    dispatchDelayedActionToast({
                        type: ToastActionType.HIDE_TOAST,
                    });
                    break;
                default:
                    dispatchDelayedActionToast({
                        type: ToastActionType.HIDE_TOAST,
                    });
                    return;
            }
        },
        [dispatchDelayedActionToast, router],
    );

    /**
     * Handles partner order toast data when received from API.
     * Shows a bill sync toast notification with partner order information.
     * Updates the global state to indicate whether partner orders should be fetched.
     *
     * @param partnerOrderToast - The toast data containing icon, message, CTA, and styling information
     * @param showMenuNudge - Whether to show menu nudge and fetch partner orders (default: false)
     */
    const onPartnerOrderDataReceived = useCallback(
        (partnerOrderToast: IPartnerOrderToast, showMenuNudge = false) => {
            if (isToastVisible) {
                return;
            }

            // Set shouldFetchPartnerOrder in global state based on showMenuNudge
            setFetchPartnerOrder(showMenuNudge);

            const {
                icon,
                message,
                cta,
                delayActionDuration = DELAYED_TOAST_CONSTANTS.DELAY_ACTION_DURATION,
            } = partnerOrderToast;

            if (isMountedRef.current) {
                dispatchDelayedActionToast({
                    type: ToastActionType.SHOW_TOAST,
                    payload: {
                        props: {
                            progressColor: partnerOrderToast?.progressColor,
                            actionIcon: icon,
                            delayActionDuration: delayActionDuration,
                            successIcon: icon,
                            actionText: message,
                            cancelText: cta?.text || DELAYED_TOAST_CONSTANTS.BUTTON_TEXT,
                            onAction: async () => {
                                // Hide the toast
                                dispatchDelayedActionToast({
                                    type: ToastActionType.HIDE_TOAST,
                                });
                            },
                            onCancel: async () => {
                                clickHandling(cta?.type || "", cta?.link);
                            },
                        },
                        style: partnerOrderToast?.style,
                    },
                });
            }
        },
        [dispatchDelayedActionToast, isToastVisible, clickHandling, setFetchPartnerOrder],
    );

    return {
        onPartnerOrderDataReceived,
    };
};
